import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  ACTIVE_MEMBERS,
  FORMER_MEMBERS,
  getManagerTagline,
} from '../utils/managers';
import { managerSlug } from '../utils/slug';
import { loadCareerRankings } from '../utils/managerStats';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import CastFigure from '../motion/CastFigure';
import Depth from '../motion/Depth';
import Reveal from '../motion/Reveal';

const CAST_SEQUENCE = ['shock', 'point', 'celebrate', 'smug'];

function ResumeLine({ stats, onPaper = false }) {
  if (!stats) return null;

  const mutedClass = onPaper ? 'text-[#765f4a]' : 'text-ink-tertiary';
  const valueClass = onPaper ? 'text-[#3d2817]' : 'text-ink-primary';

  return (
    <div className={`flex flex-wrap gap-x-4 gap-y-1 text-[9px] font-mono tracking-[0.12em] ${mutedClass}`}>
      <span>
        <strong className="text-accent-gold">{stats.stats.championships}</strong> RINGS
      </span>
      <span>
        <strong className={valueClass}>{Math.round(stats.stats.win_percentage)}%</strong> WIN
      </span>
      <span>
        <strong className={valueClass}>{stats.stats.seasons_played}</strong> YRS
      </span>
    </div>
  );
}

function CastCard({ name, stats, index }) {
  return (
    <Reveal className="h-full" delay={index * 70}>
      <Depth
        as={Link}
        strength={0.45}
        to={`/managers/${managerSlug(name)}`}
        className="depth--3d block h-full group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-gold"
        aria-label={`View ${name}'s manager profile`}
      >
        <article
          className="paper flex h-full flex-col border border-[#ded4c5] transition-colors group-hover:border-accent-gold"
          data-depth="1"
        >
          <div className="relative z-[1] flex aspect-[4/5] items-end justify-center overflow-hidden border-b border-[#d8cebf] px-2 pt-4">
            <CastFigure
              name={name}
              trigger="hover"
              sequence={CAST_SEQUENCE}
              className="h-full w-full"
              imgClassName="object-contain object-bottom"
              alt={`${name} caricature`}
            />
          </div>
          <div className="relative z-[1] flex flex-1 flex-col p-4 sm:p-5">
            <h3 className="font-display text-3xl font-black uppercase leading-[0.9] tracking-normal sm:text-4xl">
              {name}
            </h3>
            <p className="mt-2 min-h-[2.5rem] font-mono text-[9px] font-bold uppercase leading-relaxed tracking-[0.16em] text-accent-red">
              {getManagerTagline(name)}
            </p>
            <div className="mt-auto border-t border-[#d8cebf] pt-3">
              <ResumeLine stats={stats} onPaper />
            </div>
          </div>
        </article>
      </Depth>
    </Reveal>
  );
}

function AlumniCard({ name, stats, index }) {
  return (
    <Reveal className="h-full" delay={index * 90}>
      <Link
        to={`/managers/${managerSlug(name)}`}
        className="group block h-full border border-border bg-bg-surface p-5 transition-colors hover:border-accent-gold hover:bg-bg-elevated focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-gold"
        aria-label={`View ${name}'s manager profile`}
      >
        <div className="flex items-center gap-4">
          <ManagerAvatar name={name} size="xl" className="shrink-0" />
          <div className="min-w-0">
            <h3 className="font-display text-3xl font-black uppercase leading-none tracking-normal">
              {name}
            </h3>
            <p className="mt-2 font-mono text-[9px] font-bold uppercase leading-relaxed tracking-[0.16em] text-accent-gold">
              {getManagerTagline(name)}
            </p>
          </div>
        </div>
        <div className="mt-5 border-t border-border pt-4">
          <ResumeLine stats={stats} />
        </div>
      </Link>
    </Reveal>
  );
}

export default function Managers() {
  const [rankings, setRankings] = useState([]);

  useEffect(() => {
    loadCareerRankings().then(setRankings).catch(() => setRankings([]));
  }, []);

  const findStats = (name) => rankings.find((r) => r.manager === name);

  return (
    <>
      <HeroBackground backdrop="studio" variant="gold" className="px-6 py-16 sm:px-8 md:px-14 md:py-24">
        <Reveal>
          <Eyebrow>THE ROSTER</Eyebrow>
          <h1 className="mt-4 max-w-6xl font-display text-[clamp(4.5rem,13vw,11rem)] font-black uppercase leading-[0.76] tracking-normal">
            Fourteen managers.<br />
            <span className="text-accent-gold">One league.</span>
          </h1>
          <Link
            to="/stats"
            className="mt-9 inline-flex border border-accent-gold px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.22em] text-accent-gold transition-colors hover:bg-accent-gold hover:text-bg-deep"
          >
            Open all-time statistics →
          </Link>
        </Reveal>
      </HeroBackground>

      <section className="overflow-hidden border-b border-border px-4 py-14 sm:px-8 md:px-14 md:py-20">
        <Reveal>
          <h2 className="mb-8 font-mono text-[11px] font-extrabold tracking-[0.4em] text-accent-gold">
            ▾ ACTIVE · {ACTIVE_MEMBERS.length}
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-5">
          {ACTIVE_MEMBERS.map((name, index) => (
            <CastCard key={name} name={name} stats={findStats(name)} index={index} />
          ))}
        </div>
      </section>

      <section className="border-b border-border bg-bg-deep px-4 py-14 sm:px-8 md:px-14">
        <Reveal>
          <h2 className="mb-7 font-mono text-[11px] font-extrabold tracking-[0.4em] text-ink-tertiary">
            ▾ ALUMNI · {FORMER_MEMBERS.length}
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
          {FORMER_MEMBERS.map((name, index) => (
            <AlumniCard key={name} name={name} stats={findStats(name)} index={index} />
          ))}
        </div>
      </section>
    </>
  );
}
